<template>
  <div class="scanner" :class="scannerClassName">
    <div :id="readerId" class="scanner-reader"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
  type Html5QrcodeCameraScanConfig,
  type Html5QrcodeFullConfig
} from 'html5-qrcode'

// 扫码框的宽高尺寸结构，用于条形码模式下动态计算识别区域
type QrDimensions = {
  width: number
  height: number
}

// 扫码模式：qr = 二维码，barcode = 条形码
type ScannerMode = 'qr' | 'barcode'

// 二维码模式支持的码制列表
const QR_FORMATS = [Html5QrcodeSupportedFormats.QR_CODE]
// 条形码模式支持的码制列表（覆盖常见一维码）
const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E
]

const props = withDefaults(defineProps<{
  // 扫码模式，默认 qr
  mode?: ScannerMode
  // qr 模式下扫码框的边长（px），默认 300；barcode 模式下忽略此值，改用动态计算
  qrboxSize?: number
  // 识别成功后是否自动停止摄像头，默认 true；停止后画面定格，需调 restart() 才能再次扫码
  stopOnSuccess?: boolean
}>(), {
  mode: 'qr',
  qrboxSize: 300,
  stopOnSuccess: true,
})

const emit = defineEmits<{
  // 识别成功，返回码内容字符串
  success: [value: string]
  // 摄像头启动失败（权限拒绝、无摄像头等），返回可展示给用户的错误文案
  error: [message: string]
  // 摄像头成功启动、开始预览时触发
  ready: []
}>()

// 挂载扫码 DOM 容器的唯一 id，每次实例化随机生成，避免多实例冲突
const readerId = `html5-qrcode-reader-${Math.random().toString(36).slice(2)}`
// html5-qrcode 实例，null 表示尚未初始化或已被销毁
const scanner = ref<Html5Qrcode | null>(null)
// 本轮是否已触发过成功回调，防止同一码被多帧重复识别后多次 emit
const hasScanned = ref(false)
// 摄像头当前是否处于运行（start 已完成）状态，stop 时需要此标志判断是否要调 scanner.stop()
const isStarted = ref(false)
// 是否正在执行 startScanner 流程，防止并发多次调用（如快速连点重新扫码）
const isStarting = ref(false)
// 是否处于 pause 状态（实例和 DOM 保留，仅暂停识别循环）
const isPaused = ref(false)

// 根据模式给容器附加不同 class，供外部通过 CSS 差异化样式（如条形码模式用宽屏比例）
const scannerClassName = computed(() => {
  return props.mode === 'barcode' ? 'barcode-mode' : 'qr-mode'
})

// 当前模式对应的码制列表，传给 Html5Qrcode 构造函数以限制识别范围、提升速度
const formatsToSupport = computed(() => {
  return props.mode === 'barcode' ? BARCODE_FORMATS : QR_FORMATS
})

// Html5Qrcode 实例级配置（构造时传入，后续不可变）
const scannerConfig = computed<Html5QrcodeFullConfig>(() => ({
  formatsToSupport: formatsToSupport.value,
  // 禁用浏览器原生 BarcodeDetector API，统一走 html5-qrcode 自身解码，保证跨端一致性
  useBarCodeDetectorIfSupported: false,
  verbose: false
}))

// 条形码模式下动态计算扫码框尺寸：宽度夹在 [220, 360] 之间，留 12px 两侧边距；高度固定 140 适配横向条形码
const getDynamicQrbox = (viewfinderWidth: number): QrDimensions => {
  return {
    width: Math.max(Math.min(viewfinderWidth - 24, 360), 220),
    height: 140
  }
}

// 摄像头画面宽高比：条形码用 16:9（1.78），二维码用 4:3（1.33）
const aspectRatio = computed(() => {
  return props.mode === 'barcode' ? 1.777778 : 1.333334
})

// 传给 scanner.start() 的扫码运行时配置
const scanConfig = computed<Html5QrcodeCameraScanConfig>(() => ({
  // 每秒尝试识别帧数
  fps: 10,
  aspectRatio: aspectRatio.value,
  // 条形码模式用函数动态计算框尺寸，二维码模式用固定正方形
  ...(props.mode === 'barcode'
    ? {
        qrbox: getDynamicQrbox
      }
    : {
      qrbox: {
        width: props.qrboxSize,
        height: props.qrboxSize
      }
    })
}))

// 完整停止：停流 + 清除 DOM + 销毁实例，用于组件卸载或完全重建前
const stopScanner = async () => {
  if (!scanner.value) {
    return
  }

  try {
    if (isStarted.value) {
      await scanner.value.stop()
      isStarted.value = false
    }
    scanner.value.clear()
  } catch (error) {
    console.warn('[Html5QrcodeScanner] stop/clear failed', error)
  } finally {
    scanner.value = null
  }
}

// 暂停：调库内置 pause()，保留 video/canvas DOM，画面定格在最后一帧；需 resume() 才能恢复识别
const pauseScanner = () => {
  if (!scanner.value || !isStarted.value) {
    return
  }
  try {
    // shouldPauseVideo=false：仅暂停识别帧循环，video 继续渲染最后一帧；true 则同时暂停 video
    scanner.value.pause(false)
    isPaused.value = true
    // hidePausedState 是库的 private 方法，无法直接调用；通过实例属性拿到浮层元素后隐藏
    const overlay = (scanner.value as unknown as { scannerPausedUiElement: HTMLElement | null }).scannerPausedUiElement
    if (overlay) {
      overlay.style.display = 'none'
    }
  } catch (error) {
    console.warn('[Html5QrcodeScanner] pause failed', error)
  }
}

const getErrorMessage = (error: unknown) => {
  const name = typeof error === 'object' && error && 'name' in error ? String(error.name) : ''

  if (name === 'NotAllowedError') {
    return '未获得相机权限，请允许摄像头访问'
  }

  if (name === 'NotFoundError') {
    return '未检测到可用摄像头'
  }

  return '启动扫码失败，请检查摄像头权限或浏览器环境'
}

const startScanner = async () => {
  if (isStarting.value) {
    return
  }

  isStarting.value = true
  try {
    if (scanner.value) {
      await stopScanner()
    }

    hasScanned.value = false
    scanner.value = new Html5Qrcode(readerId, scannerConfig.value)
    await scanner.value.start(
      { facingMode: 'environment' },
      scanConfig.value,
      decodedText => {
        if (hasScanned.value) {
          return
        }

        hasScanned.value = true
        emit('success', decodedText)
        if (props.stopOnSuccess) {
          void pauseScanner()
        }
      },
      () => {}
    )
    isStarted.value = true
    emit('ready')
  } catch (error) {
    console.error('[Html5QrcodeScanner] start failed', error)
    emit('error', getErrorMessage(error))
  } finally {
    isStarting.value = false
  }
}

const restartScanner = async () => {
  // pause 状态下直接 resume，无需重建实例
  if (scanner.value && isPaused.value) {
    try {
      hasScanned.value = false
      isPaused.value = false
      scanner.value.resume()
      return
    } catch {
      // resume 失败则降级走完整重启
      isPaused.value = false
    }
  }
  await startScanner()
}

defineExpose({
  restart: restartScanner,
  stop: stopScanner
})

onMounted(() => {
  startScanner()
})

onBeforeUnmount(() => {
  stopScanner()
})
</script>

<style scoped>
</style>
